import { useState, useEffect, useCallback, useRef } from 'react';
import chatService from '../api/chatService';
import socketService from '../services/socketService';
import { getUser } from '../utils/auth';

/**
 * useChat Hook
 * Custom React hook for managing chat functionality
 * Combines REST API calls with Socket.IO real-time updates
 * 
 * @param {number} conversationId - Current conversation ID
 * @param {boolean} isConnected - Whether the socket is currently connected.
 *   Drives (re)registration of socket listeners so they attach once the socket
 *   finishes connecting, and re-attach after a reconnect.
 * @returns {Object} - Chat state and methods
 */
const useChat = (conversationId, isConnected = false) => {
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [onlineUsers, setOnlineUsers] = useState(new Map());
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const currentUser = getUser();
  const typingTimeoutRef = useRef(null);

  // ============= FETCH CONVERSATIONS =============
  const fetchConversations = useCallback(async (page = 1, limit = 20) => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatService.getConversations(page, limit);
      
      if (response.data.success) {
        setConversations(response.data.data.conversations || []);
      }
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setError(err.response?.data?.message || 'Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  // ============= FETCH MESSAGES =============
  const fetchMessages = useCallback(async (convId, page = 1, limit = 50, append = false) => {
    if (!convId) return;

    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError(null);
      
      const response = await chatService.getMessages(convId, page, limit);
      
      if (response.data.success) {
        const newMessages = response.data.data.messages || [];
        const hasMoreMessages = response.data.data.hasMore || false;
        
        // Backend returns DESC order (newest first), so reverse to get ASC (oldest first)
        const sortedMessages = [...newMessages].reverse();
        
        if (append && page > 1) {
          // Prepend older messages to the top
          setMessages(prev => {
            // Remove duplicates
            const existingIds = new Set(prev.map(m => m.id));
            const uniqueNewMessages = sortedMessages.filter(m => !existingIds.has(m.id));
            return [...uniqueNewMessages, ...prev];
          });
        } else {
          // Initial load - replace all messages
          setMessages(sortedMessages);
        }
        
        setCurrentPage(page);
        setHasMore(hasMoreMessages);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.message || 'Failed to fetch messages');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ============= LOAD MORE MESSAGES =============
  const loadMoreMessages = useCallback(async (convId) => {
    if (!convId || !hasMore || loadingMore) return;
    
    const nextPage = currentPage + 1;
    await fetchMessages(convId, nextPage, 50, true);
  }, [currentPage, hasMore, loadingMore, fetchMessages]);

  // ============= SEND MESSAGE =============
  const sendMessage = useCallback((messageData) => {
    if (!socketService.isSocketConnected()) {
      setError('Not connected to chat server');
      return;
    }

    // Optimistic update - add message immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}`,
      sender_id: currentUser?.id,
      receiver_id: messageData.receiver_id,
      message: messageData.message,
      message_type: messageData.message_type || 'text',
      is_read: false,
      created_at: new Date().toISOString(),
      sender: currentUser,
      isOptimistic: true,
    };

    setMessages(prev => [...prev, optimisticMessage]);

    // Send via socket
    socketService.sendMessage(messageData);
  }, [currentUser]);

  // ============= TYPING INDICATORS =============
  const startTyping = useCallback((convId) => {
    if (!convId) return;
    
    socketService.startTyping(convId);

    // Auto-stop typing after 2 seconds of inactivity
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(convId);
    }, 2000);
  }, []);

  const stopTyping = useCallback((convId) => {
    if (!convId) return;
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    socketService.stopTyping(convId);
  }, []);

  // ============= MARK AS READ =============
  const markAsRead = useCallback(async (messageId, convId) => {
    try {
      await chatService.markMessageAsRead(messageId);
      socketService.markAsRead(messageId, convId);
      
      // Update local state
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, is_read: true, read_at: new Date().toISOString() } : msg
        )
      );
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async (convId) => {
    try {
      await chatService.markAllAsRead(convId);
      
      // Update local state
      setMessages(prev => 
        prev.map(msg => ({ ...msg, is_read: true, read_at: new Date().toISOString() }))
      );
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  }, []);

  // ============= FETCH UNREAD COUNT =============
  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await chatService.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.unread_count || 0);
      }
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // ============= START CONVERSATION =============
  const startConversation = useCallback(async (userId) => {
    try {
      setLoading(true);
      setError(null);
      const response = await chatService.startConversation(userId);
      
      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      console.error('Error starting conversation:', err);
      setError(err.response?.data?.message || 'Failed to start conversation');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============= GET ONLINE STATUS =============
  const getOnlineStatus = useCallback((userId) => {
    if (!userId) return;
    console.log('🔍 Requesting online status for user:', userId);
    socketService.getOnlineStatus(userId);
  }, []);

  // ============= EDIT MESSAGE =============
  const editMessage = useCallback((messageId, newMessage, convId) => {
    if (!socketService.isSocketConnected()) {
      setError('Not connected to chat server');
      return;
    }

    // Optimistic update - preserve all message properties
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId 
          ? { 
              ...msg, 
              message: newMessage, 
              is_edited: true,
              updated_at: new Date().toISOString()
            }
          : msg
      )
    );

    // Send via socket
    socketService.editMessage(messageId, newMessage, convId);
  }, []);

  // ============= DELETE MESSAGE =============
  const deleteMessage = useCallback((messageId, convId) => {
    if (!socketService.isSocketConnected()) {
      setError('Not connected to chat server');
      return;
    }

    // Optimistic update - preserve all message properties
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId 
          ? { 
              ...msg, 
              message: 'This message was deleted', 
              is_deleted: true,
              deleted_at: new Date().toISOString()
            }
          : msg
      )
    );

    // Send via socket
    socketService.deleteMessage(messageId, convId);
  }, []);

  // ============= SOCKET EVENT HANDLERS (MEMOIZED) =============
  // These handlers are defined outside useEffect to avoid stale closures
  
  const handleNewMessage = useCallback((data) => {
    console.log('📨 New message received:', data);
    
    // If we receive a message, the sender must be online
    if (data.message?.sender_id) {
      console.log('🟢 Marking sender as online:', data.message.sender_id);
      setOnlineUsers(prev => {
        const newMap = new Map(prev);
        newMap.set(data.message.sender_id, true);
        return newMap;
      });
    }
    
    // Add message to list if it's for current conversation
    if (data.conversation_id === conversationId) {
      setMessages(prev => {
        // Check if message already exists (avoid duplicates)
        const exists = prev.some(msg => msg.id === data.message.id);
        if (exists) return prev;
        
        return [...prev, data.message];
      });
    }

    // Update conversation list
    fetchConversations();
    fetchUnreadCount();
  }, [conversationId, fetchConversations, fetchUnreadCount]);

  const handleMessageSent = useCallback((data) => {
    console.log('✅ Message sent confirmation:', data);
    
    // Replace optimistic message with real one
    setMessages(prev => 
      prev.map(msg => 
        msg.isOptimistic && msg.message === data.message.message
          ? { ...data.message, isOptimistic: false }
          : msg
      )
    );

    // Update conversation list
    fetchConversations();
  }, [fetchConversations]);

  const handleMessageRead = useCallback((data) => {
    console.log('👁️ Message read event received:', {
      message_id: data.message_id,
      conversation_id: data.conversation_id,
      read_at: data.read_at
    });
    
    // Only update if the message belongs to the current conversation
    if (data.conversation_id !== conversationId) {
      console.log('⚠️ Message read event for different conversation, skipping update');
      return;
    }
    
    setMessages(prev => {
      const messageExists = prev.some(m => m.id === data.message_id);
      console.log(`Message ${data.message_id} exists in current messages:`, messageExists);
      
      if (!messageExists) {
        console.log('⚠️ Message not in current conversation, skipping update');
        return prev;
      }
      
      const updated = prev.map(msg => {
        if (msg.id === data.message_id) {
          console.log(`✅ Updating message ${msg.id} from is_read=${msg.is_read} to is_read=true`);
          return { ...msg, is_read: true, read_at: data.read_at };
        }
        return msg;
      });
      
      return updated;
    });
  }, [conversationId]); // Add conversationId to dependencies

  const handleUserTyping = useCallback((data) => {
    console.log('⌨️ User typing:', data);
    if (data.conversation_id === conversationId) {
      setTypingUsers(prev => {
        const newSet = new Set(prev);
        if (data.is_typing) {
          newSet.add(data.user_id);
        } else {
          newSet.delete(data.user_id);
        }
        return newSet;
      });
    }
  }, [conversationId]);

  const handleUserOnline = useCallback((data) => {
    console.log('🟢 User online event received:', data);
    const userId = parseInt(data.user_id);
    setOnlineUsers(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, true);
      console.log('Updated onlineUsers map:', Array.from(newMap.entries()));
      return newMap;
    });
  }, []);

  const handleUserOffline = useCallback((data) => {
    console.log('⚫ User offline event received:', data);
    const userId = parseInt(data.user_id);
    setOnlineUsers(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, false);
      console.log('Updated onlineUsers map after offline:', Array.from(newMap.entries()));
      return newMap;
    });
  }, []);

  const handleUserOnlineStatus = useCallback((data) => {
    console.log('📊 User online status response received:', data);
    const userId = parseInt(data.user_id);
    setOnlineUsers(prev => {
      const newMap = new Map(prev);
      newMap.set(userId, data.is_online);
      console.log('Updated onlineUsers map:', Array.from(newMap.entries()));
      return newMap;
    });
  }, []);

  const handleMessageEdited = useCallback((data) => {
    console.log('✏️ Message edited:', data);
    
    if (data.conversation_id === conversationId && data.message) {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === data.message.id
            ? { ...msg, ...data.message }
            : msg
        )
      );
    }
  }, [conversationId]);

  const handleMessageDeleted = useCallback((data) => {
    console.log('🗑️ Message deleted:', data);
    
    if (data.conversation_id === conversationId && data.message) {
      setMessages(prev => 
        prev.map(msg => 
          msg.id === data.message.id
            ? { ...msg, ...data.message }
            : msg
        )
      );
    }
  }, [conversationId]);

  const handleError = useCallback((error) => {
    console.error('❌ Socket error:', error);
    setError(error.message || 'An error occurred');
  }, []);

  // ============= SOCKET EVENT LISTENERS =============
  // Register listeners and re-attach when the connection is (re)established or
  // the active conversation changes. socketService.on() is a no-op while the
  // socket doesn't exist yet, so depending on isConnected guarantees we re-run
  // once it finishes connecting. (Requesting online status lives in Messaging.js,
  // so it isn't duplicated here — that kept this effect re-running on every
  // message as the conversations list refreshed.)
  useEffect(() => {
    console.log('✅ Registering socket event listeners');

    // Register listeners
    socketService.onNewMessage(handleNewMessage);
    socketService.onMessageSent(handleMessageSent);
    socketService.onMessageRead(handleMessageRead);
    socketService.onUserTyping(handleUserTyping);
    socketService.onUserOnline(handleUserOnline);
    socketService.onUserOffline(handleUserOffline);
    socketService.onUserOnlineStatus(handleUserOnlineStatus);
    socketService.onMessageEdited(handleMessageEdited);
    socketService.onMessageDeleted(handleMessageDeleted);
    socketService.onError(handleError);

    // Cleanup listeners on unmount
    return () => {
      socketService.off('new_message', handleNewMessage);
      socketService.off('message_sent', handleMessageSent);
      socketService.off('message_read', handleMessageRead);
      socketService.off('user_typing', handleUserTyping);
      socketService.off('user_online', handleUserOnline);
      socketService.off('user_offline', handleUserOffline);
      socketService.off('user_online_status', handleUserOnlineStatus);
      socketService.off('message_edited', handleMessageEdited);
      socketService.off('message_deleted', handleMessageDeleted);
      socketService.off('error', handleError);
    };
  }, [
    isConnected,
    handleNewMessage,
    handleMessageSent,
    handleMessageRead,
    handleUserTyping,
    handleUserOnline,
    handleUserOffline,
    handleUserOnlineStatus,
    handleMessageEdited,
    handleMessageDeleted,
    handleError,
  ]);

  // ============= FETCH DATA ON MOUNT =============
  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, [fetchConversations, fetchUnreadCount]);

  useEffect(() => {
    if (conversationId) {
      // Reset pagination when conversation changes
      setCurrentPage(1);
      setHasMore(true);
      fetchMessages(conversationId, 1, 50, false);
    }
  }, [conversationId, fetchMessages]);

  return {
    // State
    conversations,
    messages,
    loading,
    error,
    typingUsers,
    onlineUsers,
    unreadCount,
    hasMore,
    loadingMore,
    currentPage,
    
    // Methods
    sendMessage,
    startTyping,
    stopTyping,
    markAsRead,
    markAllAsRead,
    fetchConversations,
    fetchMessages,
    loadMoreMessages,
    fetchUnreadCount,
    startConversation,
    getOnlineStatus,
    editMessage,
    deleteMessage,
  };
};

export default useChat;
